/**
 * WorkforceCopilot — Chris Bauer + Sarah Chen shared screen. Port of
 * schedule-copilot.jsx / generate-wizard.jsx: state a goal, get a proposed
 * plan, apply it into the draft-review flow. Ops: runCopilot /
 * applyCopilotPlan (REQ-039, kit-served). Applying creates the SAME draft
 * Sarah's Draft review reads next (§4.3 cross-op join) — the success toast
 * says exactly that.
 */
import { useState } from "react";
import { Chip, Btn, Icon, Pill, T, alpha } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { DataSourceStrip } from "../../shell/opSource";
import { toast } from "../../shell/toast";
import { can } from "../../shell/access";
import { useBusy } from "./useWorkforce";
import { runCopilot, applyCopilotPlan, type CopilotRunResult, type DraftChange } from "./wf-api";
import { mondayOf, addDaysIso, weekRangeLabel } from "./wf-shared";
import { useT, t as i18nT } from "../../lib/i18n";

/** Starter goals: sector-neutral, so they read right on any tenant (CC 2026-10-19 walk 3 item 4). */ // ui-config
const SUGGESTIONS = [
  "Cover the lunch walk-in spike without overtime",
  "Cut overtime next week while holding coverage",
  "Balance weekend coverage across the team",
  "Even out shift hours across the team",
];
const KIND_TONE: Record<DraftChange["kind"], "success" | "warn" | "primary" | "info"> = { "New shift": "success", Removed: "warn", "Skill block": "primary", "Time change": "info" };

export default function WorkforceCopilot() {
  const t = useT();
  const { busy, run } = useBusy();
  const canRun = can("copilot.run");
  const [goal, setGoal] = useState(""); // ui-local
  const [plan, setPlan] = useState<CopilotRunResult | null>(null); // ui-local (result buffer; applied via op)
  const [applied, setApplied] = useState(false); // ui-local
  const nextWeek = addDaysIso(mondayOf(), 7);

  const generate = () => {
    if (!goal.trim()) return;
    void run("run", async () => {
      try {
        setApplied(false);
        // Addendum item 17: no baked branch id — locationId is omitted so the
        // session principal scopes the run server-side (the executor clamps to
        // the caller's own branch/region; sending a literal overrode nothing
        // for the demo principal but poisoned every other tenant).
        const r = await runCopilot({ goal: goal.trim(), weekStart: nextWeek });
        setPlan(r);
      } catch (e) { toast({ tone: "danger", title: t("workforce.copilot.copilotCouldnTBuildAPlan", "Copilot couldn't build a plan"), subtitle: e instanceof Error ? e.message : String(e) }); }
    });
  };
  const apply = () => {
    if (!plan) return;
    void run("apply", async () => {
      try {
        const draft = await applyCopilotPlan(plan.planId);
        setApplied(true);
        toast({ tone: "success", title: t("workforce.copilot.planAppliedAsADraft", "Plan applied as a draft"), subtitle: i18nT("workforce.copilot.runDraftRunidNowInDraftReviewForTheBranchManager.icu", { value0: draft.runId }) });
      } catch (e) { toast({ tone: "danger", title: t("workforce.copilot.couldnTApplyThePlan", "Couldn't apply the plan"), subtitle: e instanceof Error ? e.message : String(e) }); }
    });
  };

  return (
    <Screen rail="wf-copilot">
      <PageHeader title={t("workforce.copilot.schedulingCopilot", "Scheduling Copilot")} subtitle={t("workforce.copilot.sayWhatNextWeekShouldLookLikeTheCopilotProposesScheduleChanges", "Say what next week should look like — the copilot proposes schedule changes you review and apply.")} breadcrumb={null} />
      <DataSourceStrip screen="wfcopilot" ops={["runCopilot", "applyCopilotPlan"]} reqId="REQ-039" />
      <div style={{ flex: 1, overflow: "auto", padding: "14px 24px 32px", display: "flex", flexDirection: "column", gap: 16, maxWidth: 920 }}>
        <div style={{ border: `1px solid ${T.border}`, borderRadius: 12, background: T.white, padding: "16px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
          <span style={{ fontSize: 12.5, color: T.n2 }}>{t("workforce.copilot.planning", "Planning")}{" "}<b style={{ color: T.n1 }}>{weekRangeLabel(nextWeek)}</b></span>
          <textarea data-testid="wfcopilot-goal" value={goal} rows={2} placeholder={t("workforce.copilot.eGCoverTheLunchWalkInSpikeWithoutAddingOvertime", "e.g. Cover the lunch walk-in spike without adding overtime")}
            onChange={(e) => setGoal(e.target.value)}
            style={{ width: "100%", padding: "10px 12px", border: `1px solid ${T.n5}`, borderRadius: 8, fontSize: 13.5, fontFamily: T.font, resize: "vertical", boxSizing: "border-box" }} />
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
            {SUGGESTIONS.map((s) => (
              <Chip key={s} onClick={() => setGoal(s)}>{s}</Chip>
            ))}
            <span style={{ marginLeft: "auto" }}>
              <Btn testid="wfcopilot-run" disabled={!canRun || busy !== null || !goal.trim()} title={canRun ? undefined : t("workforce.workforceCopilot.requiresTheCopilotRunCapability", "Requires the copilot.run capability")} onClick={generate}>
                <Icon.msg size={13} /> {busy === "run" ? t("events.creationCreateEventAI.thinking", "Thinking…") : t("workforce.workforceCopilot.proposeAPlan", "Propose a plan")}
              </Btn>
            </span>
          </div>
        </div>

        {plan ? (
          <div style={{ border: `1px solid ${T.border}`, borderRadius: 12, background: T.white, overflow: "hidden" }}>
            <div style={{ padding: "14px 18px", borderBottom: `1px solid ${T.n6}`, display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
              <span style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 260, flex: 1 }}>
                <span style={{ display: "flex", gap: 8, alignItems: "center" }}><b style={{ fontSize: 13.5, color: T.n1 }}>{t("workforce.copilot.proposedPlan", "Proposed plan")}</b><Pill tone="neutral">{plan.planId}</Pill>{applied ? <Pill tone="success" dot>{t("workforce.copilot.applied", "Applied")}</Pill> : null}</span>
                <span style={{ fontSize: 12.5, color: T.n2, lineHeight: 1.5 }}>{plan.summary}</span>
                {plan.provisional ? <span style={{ fontSize: 11, color: T.soft, fontStyle: "italic" }}>{plan.provisional}</span> : null}
              </span>
              <span style={{ display: "flex", gap: 18 }}>
                {[["Coverage Δ", `${plan.metrics.coverageDeltaPct > 0 ? "+" : ""}${plan.metrics.coverageDeltaPct}%`, plan.metrics.coverageDeltaPct >= 0 ? T.green1 : T.red1],
                  ["Cost Δ", `${plan.metrics.costDelta > 0 ? "+" : ""}${plan.metrics.costDelta.toFixed(1)}%`, plan.metrics.costDelta > 0 ? T.orange1 : T.green1],
                  ["Rule warnings", String(plan.metrics.violations), plan.metrics.violations > 0 ? T.orange1 : T.green1]].map(([l, v, c]) => (
                  <span key={l as string} style={{ display: "flex", flexDirection: "column", gap: 1 }}><span style={{ fontSize: 11, color: T.soft }}>{l}</span><span style={{ fontSize: 17, fontWeight: 700, fontFamily: T.fontMono, color: c as string }}>{v}</span></span>
                ))}
              </span>
            </div>
            {plan.changes.map((c) => (
              <div key={c.id} data-testid="wfcopilot-change" style={{ display: "flex", gap: 12, alignItems: "center", padding: "11px 18px", borderBottom: `1px solid ${T.n6}`, flexWrap: "wrap", fontSize: 12.5 }}>
                <span style={{ width: 130, flexShrink: 0, display: "flex", flexDirection: "column" }}><b style={{ color: T.n1, fontSize: 12.5 }}>{c.advisor}</b><span style={{ fontSize: 10.5, color: T.soft, fontFamily: T.fontMono }}>{c.day}</span></span>
                <Pill tone={KIND_TONE[c.kind]}>{c.kind}</Pill>
                <span style={{ display: "flex", gap: 8, alignItems: "center", flex: 1, minWidth: 200 }}>
                  <span style={{ fontFamily: T.fontMono, fontSize: 11.5, color: T.soft, textDecoration: "line-through" }}>{c.from}</span>
                  <span style={{ color: T.soft }}>→</span>
                  <span style={{ fontFamily: T.fontMono, fontSize: 11.5, fontWeight: 700, color: T.n1 }}>{c.to}</span>
                </span>
                <span style={{ fontSize: 11.5, color: T.soft, flexBasis: "100%", paddingLeft: 142 }}>{c.why}</span>
              </div>
            ))}
            <div style={{ padding: "13px 18px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", background: alpha(T.p1, 4) }}>
              <span style={{ fontSize: 12, color: T.soft, maxWidth: 480 }}>{t("workforce.copilot.applyingCreatesADraftRunItGoesToTheBranchSDraft", "Applying creates a draft run — it goes to the branch's Draft review for guardrail checks and publish; nothing changes on the live schedule yet.")}</span>
              <span style={{ display: "flex", gap: 8 }}>
                <Btn variant="secondary" disabled={busy !== null} onClick={() => { setPlan(null); setApplied(false); }}>{t("workforce.copilot.discard", "Discard")}</Btn>
                <Btn testid="wfcopilot-apply" disabled={busy !== null || applied} onClick={apply}><Icon.check size={13} /> {busy === "apply" ? t("workforce.workforceCopilot.applying", "Applying…") : applied ? t("workforce.copilot.applied", "Applied") : t("workforce.workforceCopilot.applyAsDraft", "Apply as draft")}</Btn>
              </span>
            </div>
          </div>
        ) : (
          <div style={{ padding: "36px 20px", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
            <span style={{ width: 48, height: 48, borderRadius: 12, background: T.p4, color: T.p1, display: "grid", placeItems: "center" }}><Icon.msg size={20} /></span>
            <span style={{ fontSize: 13, color: T.n2, maxWidth: 420, lineHeight: 1.55 }}>{t("workforce.copilot.stateAGoalAboveTheCopilotWorksFromNextWeekSReal", "State a goal above. The copilot works from next week's real shifts and demand — every proposed change names the advisor, the exact time move, and the why.")}</span>
          </div>
        )}
      </div>
    </Screen>
  );
}
